import React, { useEffect, useRef, useState } from "react";
import {
  Alert, AlertIcon, Badge, Box, Button, Center, Collapse, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
  DrawerFooter, DrawerHeader, DrawerOverlay, FormControl, FormLabel, HStack, IconButton, Input, Link, SimpleGrid,
  Spinner, Text, Tooltip, VStack, VisuallyHidden, Wrap, WrapItem, useToast,
} from "@chakra-ui/react";
import { Copy, ExternalLink, RefreshCw, RotateCcw, Wrench, EyeOff } from "lucide-react";
import ConfirmDialog from "../ConfirmDialog";
import {
  corrigirSaudeAnuncioItem, getSaudeAnunciosItem, ignorarSaudeAnuncioItem, reabrirSaudeAnuncioItem,
} from "../../services/api";
import {
  ACCENT, ACCENT_HOVER, BadgeResultado, BadgeSituacao, COR_COLETA, Miniatura, SEM_CURA, TagTipo, dataHoraBR, nomeConta, num,
  rotulo, rotuloAcao, toastDeFalha, usd, useMontado,
} from "./comum";

// O que o backend aceita em cada situação (em correção/conferência/fila de criação ele devolve 409).
const CORRIGIVEIS = ["pendente", "falhou", "manual"];
const IGNORAVEIS = ["pendente", "falhou", "manual"];
const REABRIVEIS = ["ignorado", "falhou", "manual", "corrigido", "resolvido"];
// Curas que nenhum job executa: "Corrigir agora" só relê o anúncio e decide de novo.
const CURAS_SEM_JOB = ["aguardar", "manual", "revisar_preco"];
const CURAS_HUMANAS = ["manual", "revisar_preco"];

const linkMl = (idMl) => `https://produto.mercadolivre.com.br/MLB-${String(idMl).replace(/^MLB/i, "")}`;

// Texto do ML (status, sub_status, tags) vem sem espaço: quebra em qualquer ponto em vez de estourar a gaveta.
const QUEBRA = { whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" };

/** Envio do anúncio na última leitura: a combinar, coleta ou só Flex; o resto aparece cru (modo/logística). */
const textoEnvio = (envio) => {
  if (envio.mode === "not_specified") return "A combinar";
  if (envio.mode === "me2" && envio.logistic_type === "cross_docking") return "Coleta";
  if (envio.mode === "me2" && envio.logistic_type === "self_service") return "Só Flex";
  return [envio.mode, envio.logistic_type].filter(Boolean).join("/") || "—";
};

function Secao({ titulo, children }) {
  return (
    <Box as="section" mb={5}>
      <Text as="h3" fontWeight="bold" fontSize="sm" color="gray.800" mb={2}>{titulo}</Text>
      {children}
    </Box>
  );
}

function Campo({ rotulo: titulo, children }) {
  return (
    <Box minW={0}>
      <Text fontSize="2xs" color="gray.600" textTransform="uppercase" fontWeight="bold" letterSpacing="wide">{titulo}</Text>
      <Box fontSize="sm" color="gray.800" wordBreak="break-word">{children}</Box>
    </Box>
  );
}

/** Evidências da moderação: [{secao, texto}] ou lista de textos. */
function Secoes({ secoes }) {
  return (
    <VStack align="stretch" spacing={1.5}>
      {secoes.map((s, i) => (
        <Text key={`${i}-${typeof s === "object" ? s?.secao : s}`} fontSize="sm" color="gray.700" wordBreak="break-word">
          {typeof s === "object" && s !== null
            ? <><Text as="span" fontWeight="semibold">{s.secao || "—"}</Text>{s.texto ? `: ${s.texto}` : ""}</>
            : String(s)}
        </Text>
      ))}
    </VStack>
  );
}

function DetalhesAcao({ detalhes }) {
  const [aberto, setAberto] = useState(false);
  return (
    <Box mt={1}>
      <Button size="xs" variant="link" colorScheme="blue" onClick={() => setAberto((v) => !v)} aria-expanded={aberto}
        minH={{ base: "40px", lg: "auto" }}>
        {aberto ? "Esconder detalhes" : "Ver detalhes"}
      </Button>
      <Collapse in={aberto} animateOpacity>
        <Box as="pre" mt={1} fontFamily="mono" fontSize="2xs" lineHeight="short" bg="gray.50" color="gray.800" p={2}
          borderRadius="md" whiteSpace="pre-wrap" wordBreak="break-word" maxH="280px" overflowY="auto">
          {JSON.stringify(detalhes, null, 2)}
        </Box>
      </Collapse>
    </Box>
  );
}

const temConteudo = (v) => v && typeof v === "object" && Object.keys(v).length > 0;

/**
 * O que acontece de verdade ao corrigir: respeita a simulação, mas não depende do ciclo automático.
 * `condicional` = "Reavaliar agora": só muda o anúncio se a decisão nova for uma correção.
 */
function AvisoModo({ config, condicional = false }) {
  if (!config) {
    return (
      <Text fontSize="sm" color="red.600" fontWeight="semibold">
        Não deu para confirmar se a simulação está ligada: a correção pode mudar o anúncio de verdade no Mercado Livre.
      </Text>
    );
  }
  if (config.simulacao) {
    return <Text fontSize="sm" color="purple.700">Simulação ligada: só registra o que faria, nada muda no Mercado Livre.</Text>;
  }
  return (
    <Text fontSize="sm" color="red.600" fontWeight="semibold">
      Simulação desligada: {condicional ? "se houver correção, ela muda" : "muda"} o anúncio de verdade no Mercado Livre
      {config.ativo === false ? ", mesmo com o ciclo automático desligado" : ""}.
    </Text>
  );
}

/**
 * Detalhe de um anúncio com problema: o que o ML diz, o que o sistema vai
 * fazer, o produto e o histórico. Admin pode corrigir agora, ignorar ou reabrir.
 * `item` precisa ter pelo menos `id` (da lista de anúncios ou do histórico).
 */
export default function DetalheItem({ item, rotulos, config, isAdmin, nomesContas, onFechar, onMudou }) {
  const toast = useToast();
  const montado = useMontado();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false);
  const [versao, setVersao] = useState(0);
  const [confirmar, setConfirmar] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [executando, setExecutando] = useState(false);
  // Mantém o último item durante a animação de fechar.
  const ultimo = useRef(item);
  if (item) ultimo.current = item;
  const alvo = item || ultimo.current;
  const id = item?.id;

  useEffect(() => {
    if (!id) return undefined;
    let vivo = true;
    setErro(false);
    getSaudeAnunciosItem(id).then((r) => {
      if (!vivo) return;
      if (r && r.item) setDados(r);
      else setErro(true);
    });
    return () => { vivo = false; };
  }, [id, versao]);

  // Detalhe de outro item ainda na memória não serve: espera o novo chegar.
  const atual = dados && alvo && String(dados.item?.id) === String(alvo.id) ? dados : null;
  const it = { ...(alvo || {}), ...(atual?.item || {}) };
  const produto = atual?.produto || null;
  const acoes = atual?.acoes || [];
  // Modo de agora vem junto do detalhe (a página pode estar aberta há horas); sem ele, o do painel.
  const modo = atual?.modo || config;
  // O porquê da decisão de hoje e o que cada botão faria (o backend explica pelos fatos do item).
  const previsao = atual?.previsao || null;

  const situacao = it.situacao;
  // Legado: só itens antigos têm revisar_preco, e o backend só reativa se a decisão de hoje for essa.
  const precoAtipico = it.cura === "revisar_preco" && (!atual?.previsao || atual.previsao.corrigir?.cura === "reativar");
  const forcar = precoAtipico || ["falhou", "manual"].includes(situacao);
  const podeCorrigir = isAdmin && CORRIGIVEIS.includes(situacao);
  const podeIgnorar = isAdmin && IGNORAVEIS.includes(situacao);
  const podeReabrir = isAdmin && REABRIVEIS.includes(situacao);
  const curaAoCorrigir = previsao ? previsao.corrigir?.cura : it.cura;
  const soReavalia = !precoAtipico && (previsao ? !curaAoCorrigir || CURAS_SEM_JOB.includes(curaAoCorrigir) : CURAS_SEM_JOB.includes(it.cura));
  const textoCorrigir = precoAtipico ? "Reativar mesmo assim" : soReavalia ? "Reavaliar agora" : "Corrigir agora";
  const nomeAnuncio = it.id_ml || "este produto";
  const cura = it.cura ? rotulo(rotulos, "curas", it.cura) : null;
  const curaCorrigir = curaAoCorrigir ? rotulo(rotulos, "curas", curaAoCorrigir) : null;
  // O que sai do Mercado Livre sem volta (em simulação nada muda; modo desconhecido avisa por segurança).
  const efeito = previsao?.corrigir?.efeito || null;
  const escreve = !modo || !modo.simulacao;
  const exclui = escreve && !soReavalia && efeito?.exclui ? efeito.exclui : null;
  // Revisão humana: o porquê vem da decisão (cura humana), do último erro ou da última ação que falhou/pulou.
  // Só pela situação: a cura "manual" continua no item depois de ignorar, reabrir ou resolver.
  const emRevisao = situacao === "manual";
  const acaoExplicada = acoes.find((a) => a.erro || ["falha", "pulado"].includes(a.resultado));
  const porqueDecisao = previsao?.agora && CURAS_HUMANAS.includes(previsao.agora.cura) ? previsao.agora.motivo : null;
  const textoRevisao = porqueDecisao || it.ultimo_erro || acaoExplicada?.erro || acaoExplicada?.resumo
    || (acoes.length > 0 && Number(it.tentativas) > 0
      ? "O sistema não conseguiu resolver sozinho. Veja o histórico abaixo."
      : "Este caso precisa de uma decisão humana.");
  const reabrirHumano = previsao?.reabrir && CURAS_HUMANAS.includes(previsao.reabrir.cura);
  // Item ainda pendente cuja execução o backend recusaria (produto barrado, recriado demais): diz por quê.
  const naoSegue = ["pendente", "falhou"].includes(situacao) && previsao?.corrigir
    && CURAS_HUMANAS.includes(previsao.corrigir.cura) && !CURAS_HUMANAS.includes(it.cura)
    ? previsao.corrigir.motivo : null;
  // Reabrir com o ciclo ligado e fora da simulação: a próxima rodada já executa a cura (sem outra confirmação).
  const excluiAoReabrir = escreve && modo?.ativo && !reabrirHumano ? previsao?.reabrir?.efeito?.exclui || null : null;

  const copiarMlb = async () => {
    try {
      await navigator.clipboard.writeText(it.id_ml);
      toast({ title: `${it.id_ml} copiado.`, status: "success", duration: 2000 });
    } catch (e) {
      toast({ title: "Não deu para copiar neste navegador.", status: "warning", duration: 3000 });
    }
  };

  const executar = async () => {
    if (!confirmar || !it.id) return;
    setExecutando(true);
    try {
      let r;
      // exclui_confirmado: o MLB que a pessoa viu sair (ou null). Se ao reler o anúncio a decisão
      // passar a excluir outro (ou algum), o backend não executa e pede nova confirmação.
      if (confirmar === "corrigir") r = await corrigirSaudeAnuncioItem(it.id, { forcar, exclui_confirmado: exclui });
      else if (confirmar === "ignorar") r = await ignorarSaudeAnuncioItem(it.id, motivo.trim() ? { motivo: motivo.trim() } : {});
      else r = await reabrirSaudeAnuncioItem(it.id);
      toast({ title: r?.message || "Feito.", status: "success", duration: 5000, isClosable: true });
      onMudou?.();
      if (!montado.current) return;
      setConfirmar(null);
      setMotivo("");
      setVersao((v) => v + 1);
    } catch (e) {
      toast(toastDeFalha(e, "Não foi possível"));
    } finally {
      if (montado.current) setExecutando(false);
    }
  };

  const corpoConfirmacao = {
    corrigir: (
      <VStack align="stretch" spacing={2}>
        {precoAtipico ? (
          <Text>
            O sistema segurou {nomeAnuncio} porque o preço parece fora do normal. Reative só se o preço estiver certo.
          </Text>
        ) : soReavalia ? (
          <Text>
            {it.id_ml ? `Relê ${it.id_ml} no Mercado Livre` : "Confere de novo o estoque e o bloqueio do produto"} e decide de novo.
            {naoSegue
              ? " Pelo motivo acima, o item vai para Revisão humana."
              : " Se nada mudou, continua como está. Se mudou e a decisão nova for uma correção, ela já é aplicada — exclusão de anúncio nunca sai sem você confirmar de novo."}
          </Text>
        ) : situacao === "falhou" || situacao === "manual" ? (
          <Text>
            Tenta de novo agora{curaCorrigir ? ` (${curaCorrigir})` : ""}, mesmo depois de {situacao === "falhou" ? "falhar" : "ir para Revisão humana"}.
          </Text>
        ) : (
          <Text>
            Põe {nomeAnuncio} na fila de correção agora{curaCorrigir ? ` (${curaCorrigir})` : ""}.
            {escreve ? " As travas do dia continuam valendo." : ""}
          </Text>
        )}
        {exclui && (
          <Box bg="red.50" borderLeft="3px solid" borderColor="red.400" borderRadius="md" px={3} py={2}>
            <Text fontSize="sm" color="red.700" fontWeight="semibold">Encerra e exclui {exclui} no Mercado Livre (não tem volta).</Text>
            {efeito.usa && (
              <Text fontSize="sm" color="red.700">
                {curaAoCorrigir === "excluir_duplicado" ? `Fica o ${efeito.usa}.` : `O produto passa a usar ${efeito.usa}.`}
              </Text>
            )}
            {efeito.cria && <Text fontSize="sm" color="red.700">Cria um anúncio novo no lugar: vendas e visitas do anterior não passam.</Text>}
          </Box>
        )}
        {!exclui && escreve && !soReavalia && efeito?.cria && (
          <Text fontSize="sm" color="gray.700">Cria um anúncio novo pela fila de criação.</Text>
        )}
        {!curaCorrigir && !precoAtipico && !soReavalia && <Text fontSize="sm" color="gray.600">{SEM_CURA}</Text>}
        <AvisoModo config={modo} condicional={soReavalia} />
      </VStack>
    ),
    ignorar: (
      <VStack align="stretch" spacing={3}>
        <Text>O sistema deixa de tratar {nomeAnuncio} até alguém reabrir.</Text>
        <FormControl>
          <FormLabel fontSize="sm" mb={1}>Motivo (opcional)</FormLabel>
          <Input size="md" value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={255}
            placeholder="Ex.: produto sai de linha" />
        </FormControl>
      </VStack>
    ),
    reabrir: (
      <Text>
        Volta {nomeAnuncio} para pendente e zera as tentativas.{" "}
        {reabrirHumano
          ? "Pelo que se sabe hoje, o caso continua em Revisão humana: se nada mudar no anúncio ou no produto, ele volta para lá na próxima leitura."
          : modo?.ativo ? "O ciclo automático trata de novo na próxima rodada." : "O ciclo automático trata quando estiver ligado."}
        {excluiAoReabrir && (
          <Text as="span" display="block" mt={2} color="red.700" fontWeight="semibold">
            A próxima rodada encerra e exclui {excluiAoReabrir} no Mercado Livre (não tem volta).
          </Text>
        )}
      </Text>
    ),
  };

  const tituloConfirmacao = { corrigir: textoCorrigir, ignorar: "Ignorar anúncio", reabrir: "Reabrir anúncio" };
  const botaoConfirmacao = { corrigir: textoCorrigir, ignorar: "Ignorar", reabrir: "Reabrir" };
  const temAcoes = podeCorrigir || podeIgnorar || podeReabrir;
  const ajustes = temConteudo(it.ajustes) ? it.ajustes : null;
  // Tentativa de passar o anúncio a combinar para uma categoria com coleta, e o envio que o ML mostrava na última leitura.
  const coleta = temConteudo(ajustes?.coleta) ? ajustes.coleta : null;
  const envio = temConteudo(it.envio) ? it.envio : null;
  const secoes = Array.isArray(it.secoes) ? it.secoes.filter(Boolean) : [];
  const tags = Array.isArray(it.tags) ? it.tags.filter(Boolean) : [];
  const subStatus = Array.isArray(it.sub_status) ? it.sub_status.filter(Boolean) : [];
  // Encerrado ou excluído abre uma página morta no ML.
  const noMl = !!it.id_ml && !!it.status_ml && it.status_ml !== "closed" && !subStatus.includes("deleted");
  const relacionados = [
    it.id_ml_relacionado && { rotulo: "Anúncio relacionado", valor: it.id_ml_relacionado },
    it.id_ml_novo && { rotulo: "Anúncio novo (recriado)", valor: it.id_ml_novo },
    it.fila_id && { rotulo: "Fila de criação", valor: `#${it.fila_id}` },
  ].filter(Boolean);

  return (
    <>
      <Drawer isOpen={!!item} placement="right" size={{ base: "full", md: "md" }} onClose={onFechar}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton aria-label="Fechar detalhe" size="lg" />
          <DrawerHeader pr={12} borderBottomWidth="1px">
            {alvo && (
              <HStack spacing={3} align="flex-start">
                <Miniatura src={it.imagem_url || produto?.imagem_url} tamanho="56px" />
                <Box minW={0}>
                  <HStack spacing={1}>
                    <Text as="h2" fontSize="md" fontWeight="bold" noOfLines={1}>{it.id_ml || "Sem anúncio"}</Text>
                    {it.id_ml && (
                      <Tooltip label="Copiar MLB" hasArrow>
                        <IconButton size={{ base: "md", lg: "xs" }} variant="ghost" aria-label={`Copiar ${it.id_ml}`}
                          icon={<Copy size={14} />} onClick={copiarMlb} />
                      </Tooltip>
                    )}
                  </HStack>
                  <Text fontSize="xs" color="gray.600" fontWeight="normal">
                    {it.conta_id ? nomeConta(nomesContas, it.conta_id) : "—"}{it.codigo ? ` · ${it.codigo}` : ""}
                  </Text>
                  <Text fontSize="sm" fontWeight="normal" color="gray.700" noOfLines={2}>{it.nome || produto?.nome || "—"}</Text>
                </Box>
              </HStack>
            )}
          </DrawerHeader>

          <DrawerBody pt={4} pb={8}>
            {!atual && !erro && <Center py={10}><Spinner color={ACCENT} /></Center>}
            {erro && !atual && (
              <Alert status="error" borderRadius="md" fontSize="sm" flexWrap="wrap" gap={2}>
                <AlertIcon />
                <Text flex="1" minW="180px">Não foi possível carregar este anúncio.</Text>
                <Button size={{ base: "md", lg: "sm" }} variant="outline" bg="white" leftIcon={<RefreshCw size={14} />}
                  onClick={() => setVersao((v) => v + 1)}>
                  Tentar de novo
                </Button>
              </Alert>
            )}

            {atual && (
              <>
                {erro && (
                  <Alert status="warning" borderRadius="md" fontSize="sm" mb={3}>
                    <AlertIcon />Não foi possível atualizar. Mostrando a última leitura.
                  </Alert>
                )}
                <Wrap spacing={2} mb={4} align="center">
                  <WrapItem><BadgeSituacao situacao={situacao} rotulos={rotulos} fontSize="0.75em" /></WrapItem>
                  <WrapItem><TagTipo tipo={it.tipo} rotulos={rotulos} /></WrapItem>
                  {it.status_ml && (
                    <WrapItem maxW="100%">
                      <Badge variant="outline" color="gray.700" fontSize="0.7em" sx={QUEBRA}>ML: {it.status_ml}</Badge>
                    </WrapItem>
                  )}
                  {subStatus.map((s) => (
                    <WrapItem key={s} maxW="100%">
                      <Badge variant="outline" color="gray.700" fontSize="0.7em" sx={QUEBRA}>{s}</Badge>
                    </WrapItem>
                  ))}
                  {it.fornecedor && (
                    <WrapItem maxW="100%"><Badge variant="subtle" fontSize="0.7em" sx={QUEBRA}>{it.fornecedor}</Badge></WrapItem>
                  )}
                </Wrap>

                <Secao titulo="O que o Mercado Livre diz">
                  <VStack align="stretch" spacing={2}>
                    {noMl && (
                      <Link href={linkMl(it.id_ml)} isExternal alignSelf="flex-start" display="inline-flex" alignItems="center" gap={1}
                        minH={{ base: "40px", lg: "auto" }} fontSize="sm" color="blue.600">
                        Abrir o anúncio no Mercado Livre<VisuallyHidden> (abre em outra aba)</VisuallyHidden>
                        <ExternalLink size={14} aria-hidden />
                      </Link>
                    )}
                    <Campo rotulo="Motivo">
                      {it.motivo_codigo ? (
                        <>
                          {rotulo(rotulos, "motivos", it.motivo_codigo)}
                          {rotulos?.motivos?.[it.motivo_codigo] && (
                            <Text as="span" fontSize="xs" color="gray.600"> ({it.motivo_codigo})</Text>
                          )}
                        </>
                      ) : "—"}
                    </Campo>
                    {it.motivo_texto && <Text fontSize="sm" color="gray.700" bg="gray.50" p={2} borderRadius="md">{it.motivo_texto}</Text>}
                    {secoes.length > 0 && <Campo rotulo="Evidências"><Secoes secoes={secoes} /></Campo>}
                    {envio && <Campo rotulo="Envio no ML">{textoEnvio(envio)}</Campo>}
                    {tags.length > 0 && (
                      <Campo rotulo="Tags">
                        <Wrap spacing={1} mt={1}>
                          {tags.map((t) => (
                            <WrapItem key={String(t)} maxW="100%">
                              <Badge variant="outline" color="gray.700" fontSize="0.65em" sx={QUEBRA}>{String(t)}</Badge>
                            </WrapItem>
                          ))}
                        </Wrap>
                      </Campo>
                    )}
                  </VStack>
                </Secao>

                <Secao titulo="O que o sistema vai fazer">
                  <VStack align="stretch" spacing={2}>
                    <Campo rotulo="Cura">
                      {cura || <Text as="span" color="gray.600">{SEM_CURA}</Text>}
                    </Campo>
                    {emRevisao && (
                      <Box bg="orange.50" borderLeft="3px solid" borderColor="orange.300" borderRadius="md" px={3} py={2}>
                        <Text fontSize="sm" fontWeight="semibold" color="orange.800">Precisa de alguém</Text>
                        <Text fontSize="sm" color="gray.800" wordBreak="break-word">{textoRevisao}</Text>
                      </Box>
                    )}
                    {naoSegue && (
                      <Box bg="orange.50" borderLeft="3px solid" borderColor="orange.300" borderRadius="md" px={3} py={2}>
                        <Text fontSize="sm" fontWeight="semibold" color="orange.800">Não segue sozinho</Text>
                        <Text fontSize="sm" color="gray.800" wordBreak="break-word">{naoSegue}</Text>
                      </Box>
                    )}
                    {it.remedio_texto && <Text fontSize="sm" color="gray.700">{it.remedio_texto}</Text>}
                    {it.ultimo_erro && !(emRevisao && textoRevisao === it.ultimo_erro) && (
                      <Text fontSize="sm" color="red.600" wordBreak="break-word">Último erro: {it.ultimo_erro}</Text>
                    )}
                    <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={3} mt={1}>
                      <Campo rotulo="Tentativas">{num(it.tentativas)}</Campo>
                      <Campo rotulo="Próxima ação">{dataHoraBR(it.proxima_acao_em)}</Campo>
                      <Campo rotulo="Custo de IA">{usd(it.custo_ia_usd)}</Campo>
                      <Campo rotulo="Detectado">{dataHoraBR(it.detectado_em)}</Campo>
                      <Campo rotulo="Visto">{dataHoraBR(it.visto_em)}</Campo>
                      <Campo rotulo="Corrigido">{dataHoraBR(it.corrigido_em)}</Campo>
                      {it.vendas !== null && it.vendas !== undefined && <Campo rotulo="Vendas">{num(it.vendas)}</Campo>}
                    </SimpleGrid>
                  </VStack>
                </Secao>

                {(ajustes || it.categoria_atual || it.titulo_atual) && (
                  <Secao titulo="Categoria e título">
                    <VStack align="stretch" spacing={2}>
                      {(it.categoria_atual || ajustes?.categoria_id) && (
                        <Campo rotulo="Categoria">
                          {it.categoria_atual || "—"}
                          {ajustes?.categoria_id && ajustes.categoria_id !== it.categoria_atual && (
                            <Text color="green.700">→ {ajustes.categoria_id}{ajustes.categoria_caminho ? ` (${ajustes.categoria_caminho})` : ""}</Text>
                          )}
                        </Campo>
                      )}
                      {(it.titulo_atual || ajustes?.titulo) && (
                        <Campo rotulo="Título">
                          {it.titulo_atual || "—"}
                          {ajustes?.titulo && ajustes.titulo !== it.titulo_atual && <Text color="green.700">→ {ajustes.titulo}</Text>}
                        </Campo>
                      )}
                      {ajustes?.motivo && <Campo rotulo="Por quê">{ajustes.motivo}</Campo>}
                      {coleta && (
                        <Campo rotulo="Envio por coleta">
                          <Wrap spacing={1.5} align="center" mt={0.5}>
                            <WrapItem maxW="100%">
                              <Badge colorScheme={COR_COLETA[coleta.resultado] || "gray"} textTransform="none" fontSize="xs" px={2}
                                py={0.5} borderRadius="md" sx={QUEBRA}>
                                {rotulo(rotulos, "coleta", coleta.resultado)}
                              </Badge>
                            </WrapItem>
                            {coleta.em && <WrapItem><Text fontSize="xs" color="gray.600">{dataHoraBR(coleta.em)}</Text></WrapItem>}
                          </Wrap>
                          {/* Sem categoria nova, fica só a de agora. O caminho vai mais claro para o código saltar aos olhos. */}
                          {coleta.de && (
                            <Text mt={1}>
                              {coleta.de}
                              {coleta.caminho_de && <Text as="span" color="gray.600"> ({coleta.caminho_de})</Text>}
                            </Text>
                          )}
                          {coleta.para && (
                            <Text>
                              → {coleta.para}
                              {coleta.caminho_para && <Text as="span" color="gray.600"> ({coleta.caminho_para})</Text>}
                            </Text>
                          )}
                        </Campo>
                      )}
                    </VStack>
                  </Secao>
                )}

                {relacionados.length > 0 && (
                  <Secao titulo="Relacionados">
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                      {relacionados.map((r) => <Campo key={r.rotulo} rotulo={r.rotulo}>{r.valor}</Campo>)}
                    </SimpleGrid>
                  </Secao>
                )}

                <Secao titulo="Produto">
                  {produto ? (
                    <HStack spacing={3} align="flex-start">
                      <Miniatura src={produto.imagem_url} tamanho="48px" />
                      <Box minW={0} flex="1">
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>{produto.nome || "—"}</Text>
                        <Text fontSize="xs" color="gray.600">
                          #{produto.id}{produto.codigo ? ` · ${produto.codigo}` : ""}{produto.fornecedor ? ` · ${produto.fornecedor}` : ""}
                        </Text>
                        <Wrap spacing={2} mt={1}>
                          <WrapItem><Badge colorScheme={Number(produto.estoque) > 0 ? "green" : "gray"} fontSize="0.7em">Estoque {num(produto.estoque)}</Badge></WrapItem>
                          {produto.bloqueado && <WrapItem><Badge colorScheme="red" fontSize="0.7em">Bloqueado</Badge></WrapItem>}
                        </Wrap>
                      </Box>
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.600">Sem produto vinculado no nosso cadastro.</Text>
                  )}
                </Secao>

                <Secao titulo="Histórico">
                  {acoes.length === 0 ? (
                    <Text fontSize="sm" color="gray.600">Nenhuma ação registrada ainda.</Text>
                  ) : (
                    <VStack align="stretch" spacing={3}>
                      {acoes.map((a) => (
                        <Box key={a.id} borderLeft="3px solid" borderColor={a.resultado === "falha" ? "red.300" : "gray.200"} pl={3}>
                          <Wrap spacing={1.5} align="center">
                            <WrapItem><Badge variant="subtle" fontSize="0.7em">{rotuloAcao(rotulos, a.acao)}</Badge></WrapItem>
                            <WrapItem><BadgeResultado resultado={a.resultado} rotulos={rotulos} /></WrapItem>
                            {a.simulacao && a.resultado !== "simulado" && (
                              <WrapItem><Badge colorScheme="purple" variant="outline" fontSize="0.7em">simulação</Badge></WrapItem>
                            )}
                            <WrapItem>
                              <Text fontSize="xs" color="gray.600">{dataHoraBR(a.created_at)} · {a.usuario_nome || "automático"}</Text>
                            </WrapItem>
                          </Wrap>
                          {a.resumo && <Text fontSize="sm" color="gray.800" mt={1} wordBreak="break-word">{a.resumo}</Text>}
                          {a.erro && (
                            <Text fontSize="xs" color="red.600" mt={1} wordBreak="break-word">
                              {a.http_code ? `HTTP ${a.http_code}: ` : ""}{a.erro}
                            </Text>
                          )}
                          {Number(a.custo_ia_usd) > 0 && <Text fontSize="2xs" color="gray.600" mt={0.5}>IA: {usd(a.custo_ia_usd)}</Text>}
                          {temConteudo(a.detalhes) && <DetalhesAcao detalhes={a.detalhes} />}
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Secao>
              </>
            )}
          </DrawerBody>

          {temAcoes && atual && (
            <DrawerFooter borderTopWidth="1px" gap={2} flexWrap="wrap" justifyContent="flex-start">
              {podeCorrigir && (
                <Button flex={{ base: "1 1 100%", sm: "0 0 auto" }} size="md" leftIcon={<Wrench size={16} />} bg={ACCENT} color="white"
                  _hover={{ bg: ACCENT_HOVER }} onClick={() => setConfirmar("corrigir")}>
                  {textoCorrigir}
                </Button>
              )}
              {podeReabrir && (
                <Button flex={{ base: 1, sm: "0 0 auto" }} size="md" variant="outline" leftIcon={<RotateCcw size={16} />}
                  onClick={() => setConfirmar("reabrir")}>
                  Reabrir
                </Button>
              )}
              {podeIgnorar && (
                <Button flex={{ base: 1, sm: "0 0 auto" }} size="md" variant="ghost" colorScheme="gray" leftIcon={<EyeOff size={16} />}
                  onClick={() => setConfirmar("ignorar")}>
                  Ignorar
                </Button>
              )}
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>

      <ConfirmDialog
        isOpen={!!confirmar}
        onClose={() => { if (!executando) { setConfirmar(null); setMotivo(""); } }}
        onConfirm={executar}
        isLoading={executando}
        title={tituloConfirmacao[confirmar] || "Confirmar"}
        confirmText={botaoConfirmacao[confirmar] || "Confirmar"}
        confirmColor={(confirmar === "corrigir" && exclui) || (confirmar === "reabrir" && excluiAoReabrir) ? "red" : "orange"}
        body={corpoConfirmacao[confirmar] || ""}
        buttonSize={{ base: "md", lg: "sm" }}
        wrapButtons
      />
    </>
  );
}
